/**
 * Create/Edit Listing Page - Form for creating or editing a marketplace listing
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Loader2,
  Upload,
  X,
  Package,
  PackageOpen,
  Wrench,
  Puzzle,
  MapPin,
  DollarSign,
  Info,
} from 'lucide-react';
import { api } from '../../../services/api';
import { useLanguage } from '../../../contexts/LanguageContext';

type Condition = 'excellent' | 'good' | 'fair';
type AssemblyStatus = 'flatpack_sealed' | 'flatpack_opened' | 'assembled' | 'disassembled';

interface FormData {
  product_code: string;
  asking_price: string;
  retail_price: string;
  condition: Condition;
  assembly_status: AssemblyStatus;
  times_assembled: string;
  missing_parts: string;
  modifications: string;
  description: string;
  photos: string[];
  postal_code: string;
  neighborhood: string;
}

const CONDITION_OPTIONS: { value: Condition; labelKey: string; label: string; descriptionKey: string; description: string }[] = [
  { value: 'excellent', labelKey: 'listing.condition.excellent', label: 'Excellent', descriptionKey: 'listing.condition.excellentDesc', description: 'Like new, no visible wear' },
  { value: 'good', labelKey: 'listing.condition.good', label: 'Good', descriptionKey: 'listing.condition.goodDesc', description: 'Minor signs of use, fully functional' },
  { value: 'fair', labelKey: 'listing.condition.fair', label: 'Fair', descriptionKey: 'listing.condition.fairDesc', description: 'Visible wear, may need minor repairs' },
];

const ASSEMBLY_OPTIONS: { value: AssemblyStatus; labelKey: string; label: string; icon: typeof Package }[] = [
  { value: 'flatpack_sealed', labelKey: 'listing.assembly.sealedFlatpack', label: 'Sealed Flatpack', icon: Package },
  { value: 'flatpack_opened', labelKey: 'listing.assembly.openedFlatpack', label: 'Opened Flatpack', icon: PackageOpen },
  { value: 'assembled', labelKey: 'listing.assembly.assembled', label: 'Assembled', icon: Wrench },
  { value: 'disassembled', labelKey: 'listing.assembly.disassembled', label: 'Disassembled', icon: Puzzle },
];

export default function CreateListing() {
  const { t } = useLanguage();
  const { id } = useParams<{ id?: string }>();
  const isEditing = !!id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<FormData>({
    product_code: '',
    asking_price: '',
    retail_price: '',
    condition: 'good',
    assembly_status: 'assembled',
    times_assembled: '1',
    missing_parts: '',
    modifications: '',
    description: '',
    photos: [],
    postal_code: '',
    neighborhood: '',
  });

  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [photoUploading, setPhotoUploading] = useState(false);

  // Load existing listing for editing
  const { data: existingListing, isLoading: loadingListing } = useQuery({
    queryKey: ['listing', id],
    queryFn: () => api.getMarketplaceListing(id!),
    enabled: isEditing,
  });

  // Load preferences for default location
  const { data: prefs } = useQuery({
    queryKey: ['marketplace-preferences'],
    queryFn: () => api.getMarketplacePreferences(),
  });

  useEffect(() => {
    if (existingListing?.data) {
      const listing = existingListing.data;
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Initialize form with fetched listing data for editing
      setFormData({
        product_code: listing.product_code,
        asking_price: listing.asking_price.toString(),
        retail_price: listing.retail_price?.toString() || '',
        condition: listing.condition,
        assembly_status: listing.assembly_status,
        times_assembled: listing.times_assembled?.toString() || '1',
        missing_parts: listing.missing_parts || '',
        modifications: listing.modifications || '',
        description: listing.description || '',
        photos: listing.photos || [],
        postal_code: listing.postal_code || '',
        neighborhood: listing.neighborhood || '',
      });
    }
  }, [existingListing]);

  // Set default location from preferences
  useEffect(() => {
    if (!isEditing && prefs?.data) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Initialize form defaults from user preferences
      setFormData((prev) => ({
        ...prev,
        postal_code: prev.postal_code || prefs.data.seller_postal_code || prefs.data.search_postal_code || '',
        neighborhood: prev.neighborhood || prefs.data.seller_neighborhood || '',
      }));
    }
  }, [prefs, isEditing]);

  const createMutation = useMutation({
    mutationFn: (data: Parameters<typeof api.createMarketplaceListing>[0]) =>
      api.createMarketplaceListing(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-listings'] });
      navigate('/account/marketplace');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: Parameters<typeof api.updateMarketplaceListing>[1]) =>
      api.updateMarketplaceListing(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-listings'] });
      queryClient.invalidateQueries({ queryKey: ['listing', id] });
      navigate('/account/marketplace');
    },
  });

  const handleChange = (field: keyof FormData, value: string | string[]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    if (formData.photos.length + files.length > 5) {
      alert(t('listing.photos.maxAlert', 'Maximum 5 photos allowed'));
      return;
    }

    setPhotoUploading(true);
    // For now, we'll use placeholder URLs. In production, you'd upload to storage.
    // This would typically call an upload API endpoint
    const newPhotos: string[] = [];
    for (let i = 0; i < files.length; i++) {
      // Placeholder: convert to data URL for demo
      const file = files[i];
      const reader = new FileReader();
      await new Promise<void>((resolve) => {
        reader.onload = () => {
          newPhotos.push(reader.result as string);
          resolve();
        };
        reader.readAsDataURL(file);
      });
    }
    setFormData((prev) => ({ ...prev, photos: [...prev.photos, ...newPhotos] }));
    setPhotoUploading(false);
  };

  const removePhoto = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index),
    }));
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    if (!formData.product_code.trim()) {
      newErrors.product_code = t('listing.validation.productCodeRequired', 'Product code is required');
    }

    const price = parseFloat(formData.asking_price);
    if (isNaN(price) || price <= 0) {
      newErrors.asking_price = t('listing.validation.priceRequired', 'Valid price is required');
    }

    if (!formData.postal_code.trim()) {
      newErrors.postal_code = t('listing.validation.locationRequired', 'Location is required');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const data = {
      product_code: formData.product_code,
      asking_price: parseFloat(formData.asking_price),
      retail_price: formData.retail_price ? parseFloat(formData.retail_price) : undefined,
      condition: formData.condition,
      assembly_status: formData.assembly_status,
      times_assembled: parseInt(formData.times_assembled) || 0,
      missing_parts: formData.missing_parts || undefined,
      modifications: formData.modifications || undefined,
      description: formData.description || undefined,
      photos: formData.photos,
      postal_code: formData.postal_code,
      neighborhood: formData.neighborhood || undefined,
    };

    if (isEditing) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;
  const submitError = createMutation.error || updateMutation.error;

  if (isEditing && loadingListing) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/account/marketplace')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditing ? t('listing.editTitle', 'Edit Listing') : t('listing.createTitle', 'Create Listing')}
          </h1>
          <p className="text-gray-600">
            {isEditing ? t('listing.editDescription', 'Update your listing details') : t('listing.createDescription', 'Sell your IKEA furniture')}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Product Info */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">{t('listing.productInfo.title', 'Product Information')}</h2>

          <div>
            <label htmlFor="listing-product-code" className="block text-sm font-medium text-gray-700 mb-1">
              {t('listing.productInfo.productCodeLabel', 'IKEA Product Code')} *
            </label>
            <input
              id="listing-product-code"
              type="text"
              value={formData.product_code}
              onChange={(e) => handleChange('product_code', e.target.value.toUpperCase())}
              placeholder={t('listing.productInfo.productCodePlaceholder', 'e.g., 403.515.85')}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                errors.product_code ? 'border-red-500' : 'border-gray-300'
              }`}
              disabled={isEditing}
            />
            {errors.product_code && (
              <p className="mt-1 text-sm text-red-500">{errors.product_code}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              {t('listing.productInfo.productCodeHint', 'Find this on the product label or IKEA website')}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="listing-asking-price" className="block text-sm font-medium text-gray-700 mb-1">
                <DollarSign className="w-4 h-4 inline mr-1" />
                {t('listing.productInfo.askingPriceLabel', 'Asking Price')} *
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  id="listing-asking-price"
                  type="number"
                  value={formData.asking_price}
                  onChange={(e) => handleChange('asking_price', e.target.value)}
                  placeholder="0"
                  min="0"
                  step="0.01"
                  className={`w-full pl-8 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                    errors.asking_price ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
              </div>
              {errors.asking_price && (
                <p className="mt-1 text-sm text-red-500">{errors.asking_price}</p>
              )}
            </div>

            <div>
              <label htmlFor="listing-retail-price" className="block text-sm font-medium text-gray-700 mb-1">
                {t('listing.productInfo.retailPriceLabel', 'Original Retail Price')}
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  id="listing-retail-price"
                  type="number"
                  value={formData.retail_price}
                  onChange={(e) => handleChange('retail_price', e.target.value)}
                  placeholder="0"
                  min="0"
                  step="0.01"
                  className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Condition */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">{t('listing.condition.title', 'Condition')}</h2>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {CONDITION_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleChange('condition', option.value)}
                className={`p-4 rounded-lg border-2 text-left transition-all ${
                  formData.condition === option.value
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <p className="font-medium text-gray-900">{option.label}</p>
                <p className="text-xs text-gray-500 mt-1">{option.description}</p>
              </button>
            ))}
          </div>

          <div className="pt-4">
            <span className="block text-sm font-medium text-gray-700 mb-3">
              {t('listing.assembly.title', 'Assembly Status')}
            </span>
            <div className="grid grid-cols-2 gap-3">
              {ASSEMBLY_OPTIONS.map((option) => {
                const Icon = option.icon;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleChange('assembly_status', option.value)}
                    className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                      formData.assembly_status === option.value
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="w-5 h-5 text-gray-600" />
                    <span className="text-sm font-medium">{option.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {formData.assembly_status === 'assembled' && (
            <div>
              <label htmlFor="listing-times-assembled" className="block text-sm font-medium text-gray-700 mb-1">
                {t('listing.assembly.timesAssembled', 'Times Assembled')}
              </label>
              <select
                id="listing-times-assembled"
                value={formData.times_assembled}
                onChange={(e) => handleChange('times_assembled', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                {[1, 2, 3, 4, 5].map((n) => (
                  <option key={n} value={n}>
                    {n} {n > 1 ? t('listing.assembly.times', 'times') : t('listing.assembly.time', 'time')}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label htmlFor="listing-missing-parts" className="block text-sm font-medium text-gray-700 mb-1">
              {t('listing.condition.missingParts', 'Missing Parts (if any)')}
            </label>
            <input
              id="listing-missing-parts"
              type="text"
              value={formData.missing_parts}
              onChange={(e) => handleChange('missing_parts', e.target.value)}
              placeholder={t('listing.condition.missingPartsPlaceholder', 'e.g., One screw missing, extra hardware included')}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div>
            <label htmlFor="listing-modifications" className="block text-sm font-medium text-gray-700 mb-1">
              {t('listing.condition.modifications', 'Modifications (if any)')}
            </label>
            <input
              id="listing-modifications"
              type="text"
              value={formData.modifications}
              onChange={(e) => handleChange('modifications', e.target.value)}
              placeholder={t('listing.condition.modificationsPlaceholder', 'e.g., Added custom handles')}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>

        {/* Photos */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">{t('listing.photos.title', 'Photos')}</h2>

          <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
            {formData.photos.map((photo, index) => (
              <div key={index} className="relative aspect-square">
                <img
                  src={photo}
                  alt={t('listing.photos.photoAlt', 'Photo {{num}}').replace('{{num}}', String(index + 1))}
                  className="w-full h-full object-cover rounded-lg"
                />
                <button
                  type="button"
                  onClick={() => removePhoto(index)}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}

            {formData.photos.length < 5 && (
              <label className="aspect-square border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-primary-500 hover:bg-primary-50 transition-colors">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePhotoUpload}
                  className="hidden"
                  disabled={photoUploading}
                />
                {photoUploading ? (
                  <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
                ) : (
                  <>
                    <Upload className="w-6 h-6 text-gray-400" />
                    <span className="text-xs text-gray-500 mt-1">{t('listing.photos.addPhoto', 'Add Photo')}</span>
                  </>
                )}
              </label>
            )}
          </div>

          <p className="text-xs text-gray-500 flex items-center gap-1">
            <Info className="w-3 h-3" />
            {t('listing.photos.hint', 'Add up to 5 photos. First photo will be the cover image.')}
          </p>
        </div>

        {/* Description */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">{t('listing.description.title', 'Description')}</h2>

          <textarea
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder={t('listing.description.placeholder', 'Add details about your item... Why are you selling? Any quirks buyers should know?')}
            rows={4}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        {/* Location */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">
            <MapPin className="w-5 h-5 inline mr-2" />
            {t('listing.location.title', 'Pickup Location')}
          </h2>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="listing-postal-code" className="block text-sm font-medium text-gray-700 mb-1">
                {t('listing.location.postalCodeLabel', 'Postal Code')} *
              </label>
              <input
                id="listing-postal-code"
                type="text"
                value={formData.postal_code}
                onChange={(e) => handleChange('postal_code', e.target.value)}
                placeholder={t('listing.location.postalCodePlaceholder', 'e.g., 90210')}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  errors.postal_code ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.postal_code && (
                <p className="mt-1 text-sm text-red-500">{errors.postal_code}</p>
              )}
            </div>

            <div>
              <label htmlFor="listing-neighborhood" className="block text-sm font-medium text-gray-700 mb-1">
                {t('listing.location.neighborhoodLabel', 'Neighborhood')}
              </label>
              <input
                id="listing-neighborhood"
                type="text"
                value={formData.neighborhood}
                onChange={(e) => handleChange('neighborhood', e.target.value)}
                placeholder={t('listing.location.neighborhoodPlaceholder', 'e.g., Downtown')}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          <p className="text-xs text-gray-500 flex items-center gap-1">
            <Info className="w-3 h-3" />
            {t('listing.location.hint', "Your exact address won't be shared until you arrange a meetup.")}
          </p>
        </div>

        {/* Error Message */}
        {submitError && (
          <div className="bg-red-50 text-red-700 p-4 rounded-lg">
            {submitError instanceof Error ? submitError.message : 'Failed to save listing'}
          </div>
        )}

        {/* Submit Button */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => navigate('/account/marketplace')}
            className="flex-1 px-6 py-3 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            {t('common.cancel', 'Cancel')}
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex-1 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                {t('listing.saving', 'Saving...')}
              </>
            ) : (
              isEditing ? t('listing.updateButton', 'Update Listing') : t('listing.createButton', 'Create Listing')
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
